import { Popover, Portal } from "@headlessui/react";
import { useContext, useMemo } from "react";
import {
  ComplianceProgramReportAnswerFragment,
  ComplianceProgramReportAssessmentFragment,
  GetComplianceProgramForReportQuery,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import QuestionCalibrationSection from "@apps/assessments/components/calibration/question-calibration-section";
import useAssessmentCalibrationData from "@apps/assessments/components/calibration/use-assessment-calibration-data";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertNonNull } from "@helpers/helpers";

import { isAssessmentReportAnswerType } from "./helpers";
import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";

const AssessmentAnswerCellPopover = ({
  assessmentTemplate,
  assessment,
  canCalibrate,
  canSuggestCalibration,
  styles,
  attributes,
  programHasCalibration,
  responder,
  answer,
  questionLabels,
  question,
  prevAnswerArrow,
  calibrationsStar,
  setElement,
}: {
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  assessment: ComplianceProgramReportAssessmentFragment;
  canCalibrate: boolean;
  canSuggestCalibration: boolean;
  styles: React.CSSProperties;
  attributes: React.HTMLAttributes<HTMLDivElement>;
  programHasCalibration: boolean;
  responder: BasicUser;
  answer: ComplianceProgramReportAnswerFragment;
  question: PerformanceAssessmentTableQuestion;
  questionLabels: Record<string, string>;
  prevAnswerArrow: React.ReactNode;
  calibrationsStar: React.ReactNode;
  setElement: (element: HTMLDivElement) => void;
}) => {
  const label = useLabel();
  const { complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );

  const isEmptyComment = useMemo(
    () => isEmptyValue(answer?.comment),
    [answer?.comment],
  );

  const { allCalibrations, loading: isLoadingCalibrations } =
    useAssessmentCalibrationData({
      complianceProgramId,
      templateId: assessmentTemplate.id,
      targetId: assessment.target?.id,
    });

  const answerCalibration = useMemo(() => {
    return allCalibrations.filter(
      (calibration) => calibration.question.id === question.id,
    );
  }, [allCalibrations, question.id]);

  return (
    <Portal>
      <Popover.Panel
        className="absolute left-0 z-dropdown bg-white rounded-lg border shadow-lg min-w-112 max-w-2xl max-h-[65dvh] overflow-y-scroll"
        ref={setElement}
        style={styles}
        aria-label="Assessment answer cell popover"
        {...attributes}
      >
        {({ close }) => (
          <>
            <div className="flex items-center pt-6 px-6">
              <div className="tf-header-base-dark mr-2 flex-1">
                {question.title}
              </div>
              {isLoadingCalibrations && (
                <Loading mini size={4} className="mr-2" />
              )}
              <Button
                theme={buttonTheme.iconGray}
                icon={TopicflowIconMap.close}
                onClick={() => close()}
              />
            </div>

            <div className="flex items-center gap-2 px-6 pb-6">
              {prevAnswerArrow}
              <div className="text-gray-600">
                {isAssessmentReportAnswerType(answer) &&
                  questionLabels[assertNonNull(answer.integerAnswer)]}
              </div>
              {calibrationsStar}
            </div>

            {!isEmptyComment && (
              <div className="px-6 pb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar user={responder} size={5} />
                  <div className="font-medium">
                    {responder.name}'s {label("review")} comment
                  </div>
                </div>
                <TextareaWysiwyg
                  className="text-gray-500 w-full"
                  value={answer.comment}
                  editable={false}
                />
              </div>
            )}

            {programHasCalibration && (
              <div className="border-t p-6">
                <QuestionCalibrationSection
                  complianceProgramId={complianceProgramId}
                  templateId={assessmentTemplate.id}
                  questionId={question.id}
                  answerId={answer.id}
                  targetId={assessment.target?.id}
                  calibrations={answerCalibration}
                  canCalibrate={canCalibrate}
                  canSuggestCalibration={canSuggestCalibration}
                  expanded
                  isLoadingCalibrations={isLoadingCalibrations}
                  questionLabels={questionLabels}
                />
              </div>
            )}
          </>
        )}
      </Popover.Panel>
    </Portal>
  );
};

export default AssessmentAnswerCellPopover;
